import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowUpRight, Loader2, ShieldCheck, Truck, Sparkles, Layers, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import ApparelCustomizer from "@/components/ApparelCustomizer";

const CUSTOMIZABLE_CATEGORIES = new Set(["tees", "tanks", "hoodies", "zip-ups"]);

const SHIPPING_FLAT_RATE = 14.99;
const SHIPPING_FLAT_RATE_GANGSHEETS = 9.99;
const SHIPPING_FREE_THRESHOLD = 100.0;

const TURNAROUND = {
  gangsheets: {
    days: "Overnight – 2 days",
    note: "Gang sheets are printed overnight or up to 2 days max before we ship them.",
  },
  tees: {
    days: "3 days minimum",
    note: "In-stock customized shirts ship in 3 days minimum. Out-of-stock sizes or colors take longer — we'll email an ETA before charging.",
  },
  tanks: {
    days: "3 days minimum",
    note: "In-stock customized shirts ship in 3 days minimum. Out-of-stock sizes or colors take longer — we'll email an ETA before charging.",
  },
  hoodies: {
    days: "3 days minimum",
    note: "In-stock customized shirts ship in 3 days minimum. Out-of-stock sizes or colors take longer — we'll email an ETA before charging.",
  },
  "zip-ups": {
    days: "3 days minimum",
    note: "In-stock customized shirts ship in 3 days minimum. Out-of-stock sizes or colors take longer — we'll email an ETA before charging.",
  },
  stickers: {
    days: "2 – 3 days",
    note: "Stickers are cut, laminated and packed in 2 – 3 days before shipping.",
  },
  "transfer-decals": {
    days: "2 – 3 days",
    note: "Decals are cut, laminated and packed in 2 – 3 days before shipping.",
  },
  "card-stickers": {
    days: "2 – 3 days",
    note: "Card stickers are cut, laminated and packed in 2 – 3 days before shipping.",
  },
  "label-sheets": {
    days: "2 – 3 days",
    note: "Label sheets are printed, cut and packed in 2 – 3 days before shipping.",
  },
  "door-signs": {
    days: "5 – 6 days",
    note: "Signage jobs take 5 – 6 days to complete, depending on the product.",
  },
  signage: {
    days: "5 – 6 days",
    note: "Signage jobs take 5 – 6 days to complete, depending on the product.",
  },
  marketing: {
    days: "5 – 6 days",
    note: "Marketing material lead times run 5 – 6 days depending on the specific product.",
  },
};

const BULK_TIERS = [
  { min: 51, discount: 3.05, label: "51+ items" },
  { min: 31, discount: 2.75, label: "31–50 items" },
  { min: 21, discount: 2.0, label: "21–30 items" },
  { min: 11, discount: 1.0, label: "11–20 items" },
];

function bulkDiscountForQty(qty) {
  const tier = BULK_TIERS.find((t) => qty >= t.min);
  return tier ? tier.discount : 0;
}

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [miscAck, setMiscAck] = useState(false);
  const [placements, setPlacements] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get(`/products/${productId}`)
      .then((r) => {
        if (!mounted) return;
        setProduct(r.data);
        // Default to first variant when the product ships in sizes
        if (r.data?.size_variants?.length) {
          setSelectedVariantId(r.data.size_variants[0].id);
        }
      })
      .catch(() => mounted && navigate("/shop"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [productId, navigate]);

  // Auto-rotate the image gallery every 5s when the product has multiple images
  useEffect(() => {
    if (!autoRotate) return;
    const imgs = product?.images || [];
    if (imgs.length < 2) return;
    const id = setInterval(() => {
      setSelectedImageIdx((i) => (i + 1) % imgs.length);
    }, 5000);
    return () => clearInterval(id);
  }, [autoRotate, product]);

  const handleCheckout = async () => {
    if (!product) return;
    if (product.category === "marketing" && !miscAck) {
      toast.error("Please acknowledge the 8–10 day production time to continue.");
      return;
    }
    setCheckoutLoading(true);
    try {
      // Backend only accepts location/size_inches/artwork_url — strip preview-only fields
      const cleanPlacements = placements.map((p) => ({
        location: p.location,
        size_inches: p.size_inches,
        artwork_url: p.artwork_url,
      }));
      const { data } = await api.post("/checkout/session", {
        product_id: product.id,
        quantity: qty,
        origin_url: window.location.origin,
        placements: cleanPlacements.length > 0 ? cleanPlacements : undefined,
        variant_id: selectedVariantId || undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      console.error(e);
      toast.error("Could not start checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  if (loading || !product) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="product-loading">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const isSC = product.brand === "The Sticky Chick";
  const isDTF = product.brand === "Down To Film Apparel";
  const isGangSheet = product.category === "gangsheets";
  const isMiscMarketing = product.category === "marketing";
  const isCustomizable = CUSTOMIZABLE_CATEGORIES.has(product.category);
  const isZipUp = product.category === "zip-ups";
  const hasPlacements = placements.length > 0;
  const hasVariants = Array.isArray(product.size_variants) && product.size_variants.length > 0;
  const selectedVariant = hasVariants
    ? (product.size_variants.find((v) => v.id === selectedVariantId) || product.size_variants[0])
    : null;
  const variantPrice = selectedVariant ? selectedVariant.price : product.price;
  const activeUnitPrice =
    hasPlacements && product.customized_base_price != null
      ? product.customized_base_price
      : variantPrice;
  const bulkDiscount = bulkDiscountForQty(qty);
  const effectiveUnitPrice = Math.max(0, activeUnitPrice - bulkDiscount);
  const baseSubtotal = effectiveUnitPrice * qty;
  const bulkSavings = activeUnitPrice * qty - baseSubtotal;
  // Customization fee per unit (mirrors backend authoritative logic)
  const customizationPerUnit = placements.reduce((sum, pl) => {
    if (["left-chest", "right-chest", "left-sleeve", "right-sleeve"].includes(pl.location)) {
      return sum + 3.0;
    }
    if (["front-center", "back-center"].includes(pl.location)) {
      const base = isZipUp ? 8.0 : 6.0;
      const size = pl.size_inches ?? 10.0;
      return sum + base + Math.max(0, size - 10.0) * 1.0;
    }
    return sum;
  }, 0);
  const customizationTotal = customizationPerUnit * qty;
  const subtotal = baseSubtotal + customizationTotal;
  const shippingRate =
    product.category === "gangsheets" ? SHIPPING_FLAT_RATE_GANGSHEETS : SHIPPING_FLAT_RATE;
  const freeShip = subtotal >= SHIPPING_FREE_THRESHOLD;
  const shipping = freeShip ? 0 : shippingRate;
  const total = (subtotal + shipping).toFixed(2);
  const amountToFreeShip = Math.max(0, SHIPPING_FREE_THRESHOLD - subtotal).toFixed(2);
  const canCheckout = !isMiscMarketing || miscAck;
  const turnaround = TURNAROUND[product.category];

  return (
    <div data-testid={`product-detail-${product.id}`}>
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 md:py-16">
        <Link
          to="/shop"
          className={`label-mono inline-flex items-center gap-2 ${isSC ? "hover:text-sc-pink" : "hover:text-dtf-blue"}`}
          data-testid="back-to-shop"
        >
          <ArrowLeft size={14} /> Back to shop
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-8">
          <div>
            <div className="brutal-card overflow-hidden border-black relative">
              <div className="aspect-square bg-[color:var(--muted)] relative">
                {(product.images && product.images.length > 0 ? product.images : [product.image]).map((img, i) => (
                  <img
                    key={img + i}
                    src={img}
                    alt={product.name}
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
                    style={{ opacity: i === selectedImageIdx ? 1 : 0 }}
                    data-testid={i === selectedImageIdx ? "product-main-image" : undefined}
                  />
                ))}
                {product.images && product.images.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setAutoRotate(false);
                        setSelectedImageIdx((i) => (i - 1 + product.images.length) % product.images.length);
                      }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black text-white border border-black inline-flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                      aria-label="Previous image"
                      data-testid="product-image-prev"
                    >
                      <ArrowLeft size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setAutoRotate(false);
                        setSelectedImageIdx((i) => (i + 1) % product.images.length);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black text-white border border-black inline-flex items-center justify-center hover:bg-white hover:text-black transition-colors"
                      aria-label="Next image"
                      data-testid="product-image-next"
                    >
                      <ArrowUpRight size={16} className="-rotate-45" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {product.images.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            setAutoRotate(false);
                            setSelectedImageIdx(i);
                          }}
                          className="w-2 h-2 border border-black transition-colors"
                          style={{ background: i === selectedImageIdx ? (isSC ? "var(--sc-pink)" : "var(--dtf-blue)") : "#fff" }}
                          aria-label={`Show image ${i + 1}`}
                          data-testid={`product-image-dot-${i}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            {product.images && product.images.length > 1 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="product-image-thumbs">
                {product.images.map((img, i) => {
                  const isActive = i === selectedImageIdx;
                  return (
                    <button
                      key={img + i}
                      type="button"
                      onClick={() => {
                        setAutoRotate(false);
                        setSelectedImageIdx(i);
                      }}
                      className="w-16 h-16 border-2 overflow-hidden bg-white transition-transform"
                      style={{
                        borderColor: isActive ? (isSC ? "var(--sc-pink)" : "var(--dtf-blue)") : "#000",
                        boxShadow: isActive ? `3px 3px 0 0 ${isSC ? "var(--sc-pink)" : "var(--dtf-blue)"}` : "none",
                        transform: isActive ? "translate(-1px,-1px)" : "none",
                      }}
                      aria-label={`Image ${i + 1} of ${product.images.length}`}
                      data-testid={`product-image-thumb-${i}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <span className={isSC ? "brand-tag-sc" : "brand-tag-dtf"}>
              {product.brand}
            </span>
            <h1 className={`font-display text-5xl md:text-6xl leading-[0.9] mt-3 ${isSC ? "text-sc-purple" : "text-dtf-blue"}`}>
              {product.name}
            </h1>
            <p className="font-mono text-2xl mt-6 font-bold" data-testid="product-price-header">
              ${variantPrice.toFixed(2)}
              {hasVariants && selectedVariant && (
                <span className="label-mono text-[color:var(--muted-text)]"> · {selectedVariant.label.toUpperCase()}</span>
              )}
              {product.customized_base_price != null && (
                <>
                  {" "}
                  <span className="label-mono text-[color:var(--muted-text)]">/ BLANK</span>
                  <span className="label-mono text-[color:var(--muted-text)]"> · FROM ${product.customized_base_price.toFixed(2)} CUSTOMIZED</span>
                </>
              )}
              {product.customized_base_price == null && (
                <span className="label-mono text-[color:var(--muted-text)]"> / EACH</span>
              )}
            </p>
            <p className="font-body text-[color:var(--muted-text)] mt-6">{product.description}</p>

            <div className="mt-8">
              <p className="label-mono text-[color:var(--muted-text)] mb-3">Spec Sheet</p>
              <ul className="grid grid-cols-2 gap-3">
                {product.specs.map((s) => (
                  <li key={s} className="border border-black px-3 py-2 label-mono">
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            {turnaround && (
              <div
                className="mt-6 border-2 border-black bg-white p-4 flex items-start gap-3"
                style={{ boxShadow: `4px 4px 0 0 ${isSC ? "var(--sc-pink)" : "var(--dtf-blue)"}` }}
                data-testid="turnaround-block"
              >
                <div
                  className="w-9 h-9 border border-black inline-flex items-center justify-center shrink-0"
                  style={{
                    background: isSC ? "var(--sc-pink)" : "var(--dtf-blue)",
                    color: "#fff",
                  }}
                  aria-hidden
                >
                  <Truck size={16} />
                </div>
                <div className="min-w-0">
                  <p className="label-mono text-[color:var(--muted-text)]">Production Turnaround</p>
                  <p className="font-mono text-lg font-bold" style={{ color: isSC ? "var(--sc-pink)" : "var(--dtf-blue)" }}>
                    {turnaround.days}
                  </p>
                  <p className="font-body text-sm text-[color:var(--muted-text)] mt-1">
                    {turnaround.note}
                  </p>
                </div>
              </div>
            )}

            {hasVariants && (
              <div className="mt-8" data-testid="variant-picker">
                <p className="label-mono text-[color:var(--muted-text)] mb-3">Choose Size</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {product.size_variants.map((v) => {
                    const isActive = v.id === (selectedVariant?.id);
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setSelectedVariantId(v.id)}
                        className="border-2 border-black p-3 text-left transition-colors"
                        style={{
                          background: isActive ? (isSC ? "var(--sc-pink)" : "var(--dtf-blue)") : "#fff",
                          color: isActive ? "#fff" : "#111",
                          boxShadow: isActive ? "4px 4px 0 0 #000" : "none",
                        }}
                        data-testid={`variant-${v.id}`}
                      >
                        <p className="label-mono">{v.label}</p>
                        <p className="font-mono text-lg font-bold mt-1">${v.price.toFixed(2)}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-10 flex items-end gap-4">
              <div>
                <label className="label-mono block mb-2" htmlFor="qty">Quantity</label>
                <input
                  id="qty"
                  type="number"
                  min="1"
                  max="500"
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(500, parseInt(e.target.value || "1", 10))))}
                  className="brutal-input w-28"
                  data-testid="product-qty-input"
                />
              </div>
              <div className="flex-1">
                <div className="border border-black p-4 bg-white">
                  {bulkDiscount > 0 ? (
                    <>
                      <div className="flex justify-between label-mono">
                        <span>Unit Price</span>
                        <span className="line-through text-[color:var(--muted-text)]">
                          ${variantPrice.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between label-mono mt-1">
                        <span>Bulk Price / Ea.</span>
                        <span className={isSC ? "text-sc-pink" : "text-dtf-blue"} data-testid="product-effective-unit">
                          ${effectiveUnitPrice.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between label-mono mt-1">
                        <span>Base Subtotal</span>
                        <span data-testid="product-base-subtotal">${baseSubtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between label-mono mt-1">
                        <span>You Save</span>
                        <span className={isSC ? "text-sc-pink" : "text-dtf-blue"} data-testid="product-savings">
                          −${bulkSavings.toFixed(2)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between label-mono">
                      <span>Base Subtotal</span>
                      <span data-testid="product-base-subtotal">${baseSubtotal.toFixed(2)}</span>
                    </div>
                  )}
                  {customizationTotal > 0 && (
                    <div className="flex justify-between label-mono mt-2" data-testid="product-customization-row">
                      <span>
                        Customization ({placements.length} × ${customizationPerUnit.toFixed(2)})
                      </span>
                      <span className={isSC ? "text-sc-pink" : "text-dtf-blue"}>
                        +${customizationTotal.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between label-mono mt-2 pt-2 border-t border-black">
                    <span>Subtotal</span>
                    <span data-testid="product-subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between label-mono mt-2">
                    <span>Shipping</span>
                    {freeShip ? (
                      <span
                        className={isSC ? "text-sc-pink" : "text-dtf-blue"}
                        data-testid="product-shipping"
                      >
                        FREE
                      </span>
                    ) : (
                      <span data-testid="product-shipping">
                        ${shippingRate.toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-black">
                    <span className="label-mono">Total</span>
                    <span
                      className={`font-display text-3xl ${isSC ? "text-sc-purple" : "text-dtf-blue"}`}
                      data-testid="product-total"
                    >
                      ${total}
                    </span>
                  </div>
                </div>
                {!freeShip && (
                  <p className="label-mono text-[10px] text-[color:var(--muted-text)] mt-2" data-testid="ship-nudge">
                    Add <span className={isSC ? "text-sc-pink" : "text-dtf-blue"}>${amountToFreeShip}</span> more for FREE shipping
                  </p>
                )}
                {bulkDiscount === 0 && (
                  <p className="label-mono text-[10px] text-[color:var(--muted-text)] mt-2" data-testid="bulk-nudge">
                    Order <span className={isSC ? "text-sc-pink" : "text-dtf-blue"}>11+</span> to save $1/each · <span className={isSC ? "text-sc-pink" : "text-dtf-blue"}>21+</span> saves $2 · <span className={isSC ? "text-sc-pink" : "text-dtf-blue"}>31+</span> saves $2.75 · <span className={isSC ? "text-sc-pink" : "text-dtf-blue"}>51+</span> saves $3.05
                  </p>
                )}
              </div>
            </div>

            {isCustomizable && (
              <ApparelCustomizer
                product={product}
                placements={placements}
                onChange={setPlacements}
                brandColor="var(--dtf-blue)"
              />
            )}

            {isMiscMarketing && (
              <div
                className="mt-8 border-2 border-black p-5 bg-white"
                style={{ boxShadow: "6px 6px 0 0 #111" }}
                data-testid="misc-marketing-contract"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-black text-white inline-flex items-center justify-center shrink-0">
                    <AlertTriangle size={20} />
                  </div>
                  <div className="flex-1">
                    <p className="label-mono">PRODUCTION NOTICE</p>
                    <h4 className="font-display text-2xl leading-none mt-1">
                      8–10 DAY TURNAROUND
                    </h4>
                    <p className="font-body text-[color:var(--muted-text)] text-sm mt-2">
                      Misc marketing materials do <strong className="text-black">not</strong> ship on our
                      standard 2-day production timeline. Please allow <strong className="text-black">8–10 business days</strong>
                      {" "}for production before shipping. You must acknowledge this before placing your order.
                    </p>
                    <label
                      className="flex items-start gap-3 mt-4 cursor-pointer select-none"
                      data-testid="misc-marketing-ack-label"
                    >
                      <input
                        type="checkbox"
                        checked={miscAck}
                        onChange={(e) => setMiscAck(e.target.checked)}
                        className="w-5 h-5 accent-black mt-0.5 shrink-0"
                        data-testid="misc-marketing-ack-checkbox"
                      />
                      <span className="font-body text-sm">
                        I understand and agree that this order will take{" "}
                        <strong>8–10 business days</strong> to produce before it ships.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-4">
              <button
                onClick={handleCheckout}
                disabled={checkoutLoading || !canCheckout}
                className={`btn-brutal ${isSC ? "btn-brutal-sc" : "btn-brutal-dtf"} disabled:opacity-60 disabled:cursor-not-allowed`}
                data-testid="buy-now-btn"
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Starting checkout...
                  </>
                ) : (
                  <>Buy Now — Secure Checkout</>
                )}
              </button>
              <Link to="/quote" className="btn-brutal btn-brutal-outline" data-testid="request-quote-btn">
                Need a Custom Job? Get a Quote
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-10">
              <div className="flex items-center gap-2 label-mono">
                <ShieldCheck size={16} className={isSC ? "text-sc-pink" : "text-dtf-blue"} /> American Owned-Operated
              </div>
              <div className="flex items-center gap-2 label-mono">
                <Truck size={16} className={isSC ? "text-sc-pink" : "text-dtf-blue"} /> Fast Shipping
              </div>
              <div className="flex items-center gap-2 label-mono">
                <Sparkles size={16} className={isSC ? "text-sc-pink" : "text-dtf-blue"} /> Quality Prints
              </div>
            </div>

            {isDTF && (
              <div
                className="mt-10 border border-black p-6 relative"
                style={{
                  background:
                    "linear-gradient(135deg, var(--dtf-yellow-soft) 0%, transparent 100%)",
                  boxShadow: "6px 6px 0 0 var(--dtf-blue)",
                }}
                data-testid="gangsheet-upsell"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-dtf-yellow border border-dtf-blue inline-flex items-center justify-center shrink-0">
                    <Layers size={22} className="text-dtf-blue" />
                  </div>
                  <div className="flex-1">
                    <p className="label-mono text-dtf-blue">
                      {isGangSheet ? "Other Sizes" : "Also Available"}
                    </p>
                    <h3 className="font-display text-2xl md:text-3xl mt-1 text-dtf-blue leading-none">
                      {isGangSheet ? "SHOP ALL GANG SHEET SIZES" : "PRESS YOUR OWN — DTF GANG SHEETS"}
                    </h3>
                    <p className="font-body text-[color:var(--muted-text)] mt-2 text-sm">
                      {isGangSheet
                        ? "Our gang sheets come in one standard 14\" x 18\" size — packed with as many of your designs as you can fit. Bring your own blanks and heat press."
                        : "Have your own blanks and a heat press? Order a 14\" x 18\" DTF gang sheet packed with your designs and print unlimited shirts on your schedule."}
                    </p>
                    <Link
                      to="/shop?category=gangsheets"
                      className="btn-brutal btn-brutal-dtf mt-5"
                      data-testid="gangsheet-upsell-link"
                    >
                      {isGangSheet ? "View Gang Sheet" : "Shop Gang Sheets"} <ArrowUpRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
